import React, { useState, useRef, useEffect } from "react";
import { Dices, SkipForward, Zap, LayoutGrid, List } from "lucide-react";
import { FplPitch } from "./fpl/FplPitch";
import { FplListView } from "./fpl/FplListView";
import { ShirtKit } from "./fpl/ShirtKit";
import { armbandsFor, withCaptain, pkey } from "./fpl/fplUtils";
import { clubs, seasons, randomSpin, simulateSeason, slotAccepts } from "../data/gameData";
import { useToast } from "../hooks/use-toast";

export const FORMATIONS = {
  "433": {
    label: "4-3-3",
    slots: [
      { id: 0, cat: "GK", label: "GK", x: 50, y: 90 },
      { id: 1, cat: "DEF", label: "LB", x: 13, y: 71 },
      { id: 2, cat: "DEF", label: "CB", x: 38, y: 74 },
      { id: 3, cat: "DEF", label: "CB", x: 62, y: 74 },
      { id: 4, cat: "DEF", label: "RB", x: 87, y: 71 },
      { id: 5, cat: "MID", label: "CM", x: 26, y: 50 },
      { id: 6, cat: "MID", label: "CM", x: 50, y: 53 },
      { id: 7, cat: "MID", label: "CM", x: 74, y: 50 },
      { id: 8, cat: "FWD", label: "LW", x: 20, y: 27 },
      { id: 9, cat: "FWD", label: "ST", x: 50, y: 22 },
      { id: 10, cat: "FWD", label: "RW", x: 80, y: 27 },
    ],
  },
  "442": {
    label: "4-4-2",
    slots: [
      { id: 0, cat: "GK", label: "GK", x: 50, y: 90 },
      { id: 1, cat: "DEF", label: "LB", x: 13, y: 72 },
      { id: 2, cat: "DEF", label: "CB", x: 38, y: 75 },
      { id: 3, cat: "DEF", label: "CB", x: 62, y: 75 },
      { id: 4, cat: "DEF", label: "RB", x: 87, y: 72 },
      { id: 5, cat: "MID", label: "LM", x: 13, y: 50 },
      { id: 6, cat: "MID", label: "CM", x: 38, y: 52 },
      { id: 7, cat: "MID", label: "CM", x: 62, y: 52 },
      { id: 8, cat: "MID", label: "RM", x: 87, y: 50 },
      { id: 9, cat: "FWD", label: "ST", x: 36, y: 25 },
      { id: 10, cat: "FWD", label: "ST", x: 64, y: 25 },
    ],
  },
  "352": {
    label: "3-5-2",
    slots: [
      { id: 0, cat: "GK", label: "GK", x: 50, y: 90 },
      { id: 1, cat: "DEF", label: "CB", x: 27, y: 73 },
      { id: 2, cat: "DEF", label: "CB", x: 50, y: 75 },
      { id: 3, cat: "DEF", label: "CB", x: 73, y: 73 },
      { id: 4, cat: "MID", label: "LM", x: 11, y: 50 },
      { id: 5, cat: "MID", label: "CM", x: 31, y: 53 },
      { id: 6, cat: "MID", label: "CM", x: 50, y: 55 },
      { id: 7, cat: "MID", label: "CM", x: 69, y: 53 },
      { id: 8, cat: "MID", label: "RM", x: 89, y: 50 },
      { id: 9, cat: "FWD", label: "ST", x: 36, y: 25 },
      { id: 10, cat: "FWD", label: "ST", x: 64, y: 25 },
    ],
  },
};

const buildXi = (key) => FORMATIONS[key].slots.map((s) => ({ ...s, player: null }));

const catColor = { GK: "#f1c40f", DEF: "#3498db", MID: "#2ecc71", FWD: "#e74c3c" };

const DraftBoard = ({ onSimulate, onBoardChange, ctaLabel = "Simulate the Season", initialXi, initialFormation, subtitle }) => {
  const { toast } = useToast();
  const [formation, setFormation] = useState(initialFormation || "433");
  const [xi, setXi] = useState(() => initialXi || buildXi(initialFormation || "433"));
  const [spun, setSpun] = useState(null);
  const [spinning, setSpinning] = useState(false);
  const [reel, setReel] = useState(clubs[0]);
  const [pendingPlayer, setPendingPlayer] = useState(null);
  const [simulated, setSimulated] = useState(false);
  const [view, setView] = useState("pitch");
  const [captainKey, setCaptainKey] = useState(null);
  const intervalRef = useRef(null);

  const filled = xi.filter((s) => s.player).length;
  const draftedNames = new Set(xi.filter((s) => s.player).map((s) => s.player.name.toLowerCase()));

  // Live unit ratings — recomputed every time the XI changes.
  const unitRating = (cats) => {
    const ps = xi.filter((s) => s.player && cats.includes(s.player.cat)).map((s) => s.player.rating);
    return ps.length ? Math.round(ps.reduce((a, b) => a + b, 0) / ps.length) : null;
  };
  const unitCount = (cats) => ({
    filled: xi.filter((s) => s.player && cats.includes(s.cat)).length,
    total: xi.filter((s) => cats.includes(s.cat)).length,
  });
  const UNITS = [
    { key: "att", label: "Attack", color: "#e74c3c", cats: ["FWD"] },
    { key: "mid", label: "Midfield", color: "#2ecc71", cats: ["MID"] },
    { key: "def", label: "Defence", color: "#3498db", cats: ["GK", "DEF"] },
  ];

  // Broadcast board state to the parent (used for live split-screen multiplayer).
  useEffect(() => {
    if (onBoardChange) onBoardChange({ formation, xi: withCaptain(xi, captainKey), filled });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [xi, formation, captainKey]);

  const changeFormation = (key) => {
    if (key === formation) return;
    if (filled > 0) {
      toast({ title: "Formation locked", description: "You've started drafting — reset to pick a new shape." });
      return;
    }
    setFormation(key);
    setXi(buildXi(key));
    setSpun(null);
    setPendingPlayer(null);
  };

  const doSpin = () => {
    if (spinning || filled === 11) return;
    setSpinning(true);
    setSpun(null);
    let ticks = 0;
    intervalRef.current = setInterval(() => {
      setReel(clubs[Math.floor(Math.random() * clubs.length)]);
      ticks++;
      if (ticks > 18) {
        clearInterval(intervalRef.current);
        const landed = randomSpin();
        setReel(landed.club);
        setSpun(landed);
        setSpinning(false);
      }
    }, 90);
  };

  const placePlayer = (player, slotId) => {
    const next = xi.map((s) => (s.id === slotId ? { ...s, player } : s));
    setXi(next);
    setSpun(null);
    setPendingPlayer(null);
  };

  const selectPlayer = (player) => {
    if (draftedNames.has(player.name.toLowerCase())) {
      toast({ title: "Already in your XI", description: `${player.name} is already drafted.` });
      return;
    }
    const eligible = xi.filter((s) => !s.player && slotAccepts(s, player));
    if (eligible.length === 0) {
      toast({ title: `No open ${player.pos} slot`, description: "Spin again or fill a different position." });
      return;
    }
    if (eligible.length === 1) {
      placePlayer(player, eligible[0].id);
    } else {
      setPendingPlayer(player);
    }
  };

  const skipSpin = () => {
    setSpun(null);
    setPendingPlayer(null);
  };

  const simulate = () => {
    if (filled < 11 || simulated) return;
    setSimulated(true);
    const capXi = withCaptain(xi, captainKey);
    onSimulate(simulateSeason(capXi), capXi);
  };

  const ranked = xi.filter((s) => s.player).map((s) => s.player);
  const armbands = armbandsFor(withCaptain(xi, captainKey));
  const makeCaptain = (key) => {
    if (simulated) return;
    setCaptainKey(key);
    const p = ranked.find((r) => pkey(r) === key);
    if (p) toast({ title: "Captain set", description: `${p.name} wears the armband — rating counts double.` });
  };
  const capP = ranked.find((p) => armbands[pkey(p)] === "C");
  const teamRating = ranked.length
    ? Math.round((ranked.reduce((a, p) => a + p.rating, 0) + (capP ? capP.rating : 0)) / (ranked.length + (capP ? 1 : 0)))
    : null;
  const pitchProps = { xi, armbands, pendingPlayer, slotAccepts, onPlace: placePlayer, onCaptain: simulated ? undefined : makeCaptain };

  return (
    <div className="max-w-5xl mx-auto px-4 py-6 grid lg:grid-cols-[1fr_360px] gap-6">
      {/* Pick Team (FPL-style) */}
      <div>
        <div className="mb-3">
          <h2 className="font-display font-extrabold text-2xl sm:text-3xl text-white" data-testid="pick-team-title">
            Pick Team
          </h2>
          <div className="text-muted-38 text-sm">{subtitle || "Spin a club, sign one player per spin, fill your XI."}</div>
          {filled > 0 && (
            <div data-testid="captain-hint" className="text-[#10B981] text-xs font-semibold mt-1">
              Tap any shirt to make them captain — their rating counts double.
            </div>
          )}
        </div>

        {/* Gameweek-style status bar */}
        <div
          data-testid="team-status-bar"
          className="grid grid-cols-3 rounded-xl overflow-hidden mb-3 text-[#0F172A]"
          style={{ background: "#10B981" }}
        >
          {[
            { k: "Drafted", v: `${filled}/11`, id: "status-drafted" },
            { k: "Team Rating", v: teamRating ?? "\u2013", id: "status-team-rating" },
            { k: "Formation", v: FORMATIONS[formation].label, id: "status-formation" },
          ].map((s, i) => (
            <div key={s.k} className={`px-3 py-2.5 text-center ${i ? "border-l border-[#0F172A]/15" : ""}`}>
              <div className="text-[10px] sm:text-[11px] font-bold uppercase tracking-wide opacity-75">{s.k}</div>
              <div data-testid={s.id} className="font-display font-extrabold text-xl sm:text-2xl tabular-nums">{s.v}</div>
            </div>
          ))}
        </div>

        {/* Formation picker + view toggle */}
        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
          <div className="inline-flex items-center gap-1 rounded-xl bg-white/5 border border-white/10 p-1">
            {Object.entries(FORMATIONS).map(([key, f]) => {
              const locked = filled > 0 && key !== formation;
              return (
                <button
                  key={key}
                  onClick={() => changeFormation(key)}
                  disabled={locked}
                  data-testid={`formation-${key}`}
                  className={`rounded-lg px-3 py-1.5 text-sm font-semibold transition-colors ${
                    formation === key
                      ? "bg-[#10B981] text-[#0F172A]"
                      : locked
                      ? "text-slate-600 cursor-not-allowed"
                      : "text-slate-300 hover:text-white hover:bg-white/5"
                  }`}
                >
                  {f.label}
                </button>
              );
            })}
          </div>
          <div className="inline-flex rounded-xl bg-white/5 border border-white/10 p-1" data-testid="view-toggle">
            {[
              { k: "pitch", label: "Pitch View", Icon: LayoutGrid },
              { k: "list", label: "List View", Icon: List },
            ].map(({ k, label, Icon }) => (
              <button
                key={k}
                data-testid={`view-${k}-btn`}
                onClick={() => setView(k)}
                className={`rounded-lg px-3 py-1.5 text-sm font-semibold flex items-center gap-1.5 transition-colors ${
                  view === k ? "bg-white text-[#0F172A]" : "text-slate-300 hover:text-white hover:bg-white/5"
                }`}
              >
                <Icon className="w-4 h-4" /> {label}
              </button>
            ))}
          </div>
        </div>

        {pendingPlayer && (
          <div className="mb-3 rounded-xl border border-[#10B981]/40 bg-[#10B981]/10 px-4 py-2.5 flex items-center justify-between gap-3">
            <span className="text-sm text-[#10B981] font-medium">
              Tap a highlighted {pendingPlayer.cat} slot to place {pendingPlayer.name}
            </span>
            <button onClick={() => setPendingPlayer(null)} className="text-xs text-slate-300 hover:text-white underline shrink-0">
              Cancel
            </button>
          </div>
        )}

        {view === "pitch" ? <FplPitch {...pitchProps} /> : <FplListView {...pitchProps} />}

        {/* Bench-style strip: live unit ratings */}
        <div className="rounded-b-xl px-3 pt-2 pb-3" style={{ background: "#7fe0a8" }} data-testid="rating-panel">
          <div className="text-[10px] font-bold uppercase tracking-wide text-[#0F172A]/70 mb-1.5">Unit Ratings</div>
          <div className="grid grid-cols-3 gap-2">
            {UNITS.map((u) => {
              const val = unitRating(u.cats);
              const c = unitCount(u.cats);
              return (
                <div key={u.key} data-testid={`rating-${u.key}`} className="rounded-md overflow-hidden text-center bg-white/90">
                  <div className="text-[10px] font-bold uppercase py-0.5 text-white" style={{ background: "#0F172A" }}>
                    {u.label}
                  </div>
                  <div data-testid={`rating-${u.key}-value`} className="font-display font-extrabold text-2xl text-[#0F172A] tabular-nums">
                    {val ?? "\u2013"}
                  </div>
                  <div className="text-[10px] text-[#0F172A]/60 pb-1">
                    {c.filled}/{c.total} picked
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <button
          data-testid="draft-simulate-btn"
          onClick={simulate}
          disabled={filled < 11 || simulated}
          className={`w-full mt-4 rounded-2xl py-4 font-semibold text-base flex items-center justify-center gap-2 transition-all ${
            filled < 11 || simulated ? "bg-white/5 text-slate-500 cursor-not-allowed border border-white/10" : "btn-green"
          }`}
        >
          <Zap className="w-5 h-5" />
          {simulated ? "Season simulated" : filled < 11 ? `Draft ${11 - filled} more to simulate` : ctaLabel}
        </button>
      </div>

      {/* Draft panel */}
      <div className="lg:sticky lg:top-20 lg:self-start">
        <div className="card-38 p-0 overflow-hidden hover:translate-y-0">
          <div className="p-5">
          <div className="text-center">
            <div className="text-muted-38 text-[11px] font-semibold label-eyebrow mb-3">
              {spun ? "You landed on" : spinning ? "Spinning..." : "Spin to draft"}
            </div>
            <div
              className={`mx-auto w-full rounded-xl border border-white/10 py-5 px-4 transition-transform ${spinning ? "scale-[1.02]" : ""}`}
              style={{ backgroundColor: (spun ? spun.club.color : reel.color) + "22" }}
            >
              <ShirtKit
                short={spun ? spun.club.short : reel.short}
                color={spun ? spun.club.color : reel.color}
                className="mx-auto w-16 h-16 mb-1"
              />
              <div className="font-display font-bold text-white text-lg leading-tight">
                {spun ? spun.club.name : reel.name}
              </div>
              <div className="text-muted-38 text-sm mt-0.5">{spun ? spun.season : seasons[0]}</div>
            </div>
          </div>

          {!spun ? (
            <button
              data-testid="draft-spin-btn"
              onClick={doSpin}
              disabled={spinning || filled === 11}
              className={`w-full mt-4 rounded-xl py-3.5 font-semibold flex items-center justify-center gap-2 ${
                spinning || filled === 11 ? "bg-white/5 text-slate-500 border border-white/10 cursor-not-allowed" : "btn-green"
              }`}
            >
              <Dices className="w-5 h-5" />
              {filled === 11 ? "XI Complete" : spinning ? "Spinning..." : "Spin the wheel"}
            </button>
          ) : (
            <>
              <div className="mt-4 space-y-2 max-h-[340px] overflow-y-auto pr-1">
                {[...spun.squad]
                  .map((p) => {
                    const drafted = draftedNames.has(p.name.toLowerCase());
                    const available = !drafted && xi.some((s) => !s.player && slotAccepts(s, p));
                    return { p, drafted, available };
                  })
                  // Draftable players float to the top; full/unavailable positions sink to the bottom.
                  .sort((a, b) => (b.available ? 1 : 0) - (a.available ? 1 : 0))
                  .map(({ p, drafted, available }) => (
                    <button
                      key={p.id}
                      data-testid={`draft-player-${p.id}`}
                      onClick={() => selectPlayer(p)}
                      disabled={!available}
                      className={`w-full flex items-center gap-3 rounded-lg border px-3 py-2 text-left transition-colors ${
                        available
                          ? "border-white/10 bg-white/[0.03] hover:bg-white/[0.07] hover:border-[#10B981]/50"
                          : "border-white/5 bg-white/[0.01] opacity-40 cursor-not-allowed"
                      }`}
                    >
        <span
                        className="w-8 h-8 rounded-md flex items-center justify-center text-[10px] font-bold text-white shrink-0"
                        style={{ backgroundColor: catColor[p.cat] }}
                      >
                        {p.pos}
                      </span>
                      <span className="flex-1 min-w-0">
                        <span className="block text-white text-sm font-medium truncate">{p.name}</span>
                        <span className="block text-muted-38 text-xs">{p.club}</span>
                      </span>
                      {drafted ? (
                        <span className="text-[10px] font-semibold text-slate-500 shrink-0">In XI</span>
                      ) : available ? (
                        <span className="font-display font-bold text-[#10B981] shrink-0">{p.rating}</span>
                      ) : (
                        <span className="text-[10px] font-semibold text-slate-500 shrink-0">Slot full</span>
                      )}
                    </button>
                  ))}
              </div>
              {!spun.squad.some(
                (p) => !draftedNames.has(p.name.toLowerCase()) && xi.some((s) => !s.player && slotAccepts(s, p))
              ) && (
                <button
                  onClick={skipSpin}
                  className="w-full mt-3 btn-ghost rounded-xl py-2.5 text-sm font-medium text-slate-300 flex items-center justify-center gap-2"
                >
                  <SkipForward className="w-4 h-4" /> No eligible players — spin again
                </button>
              )}
            </>
          )}
          </div>
        </div>

        <p className="text-muted-38 text-xs text-center mt-3 px-2">
          Real clubs & players with independent game ratings. Spin, draft one player per club, fill all 11 slots, then simulate.
        </p>
      </div>
    </div>
  );
};

export default DraftBoard;
